import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SessionUser } from '../../shared/types';
import { secondsUntilExpiry, userFromToken } from './jwt';

const TOKEN_KEY = 'cw_idtoken';
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const DEV_DATA = import.meta.env.VITE_DEV_DATA === 'true';

type Status = 'loading' | 'signedOut' | 'signedIn';

interface AuthValue {
  status: Status;
  user: SessionUser | null;
  /** True once GIS has loaded and a client ID is configured. */
  googleReady: boolean;
  /** Set when no VITE_GOOGLE_CLIENT_ID is configured. */
  configError: string | null;
  /** True in the local no-auth dev mode (VITE_DEV_DATA=true). */
  devMode: boolean;
  /** Whether the signed-in user may manage the allowlist (from /api/me). */
  isAdmin: boolean;
  /** Render the official Google button into `el`. */
  renderButton: (el: HTMLElement) => void;
  signOut: () => void;
  /** fetch() that attaches the ID token and signs out on 401. */
  authedFetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [googleReady, setGoogleReady] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const tokenRef = useRef<string | null>(null);
  const expiryTimer = useRef<number | undefined>(undefined);
  const initialized = useRef(false);

  const configError =
    !DEV_DATA && !CLIENT_ID
      ? 'Kein Google-Client-ID konfiguriert (VITE_GOOGLE_CLIENT_ID).'
      : null;

  const clearTimer = () => {
    if (expiryTimer.current) window.clearTimeout(expiryTimer.current);
    expiryTimer.current = undefined;
  };

  const signOut = useCallback(() => {
    clearTimer();
    tokenRef.current = null;
    localStorage.removeItem(TOKEN_KEY);
    try {
      // Stop auto re-login after an explicit sign-out.
      window.google?.accounts.id.disableAutoSelect();
    } catch {
      /* GIS not loaded — ignore */
    }
    setUser(null);
    setIsAdmin(false);
    setStatus('signedOut');
  }, []);

  // Ask GIS for a fresh ID token without UI (works for returning users who are
  // still signed in to Google and previously consented — auto_select).
  const silentRefresh = useCallback(() => {
    try {
      window.google?.accounts.id.prompt();
    } catch {
      /* GIS not loaded — ignore; a 401 will surface re-auth if needed */
    }
  }, []);

  // Adopt a freshly received / restored ID token.
  const adoptToken = useCallback(
    (token: string) => {
      const u = userFromToken(token);
      const ttl = secondsUntilExpiry(token);
      if (!u || ttl <= 0) {
        signOut();
        return;
      }
      tokenRef.current = token;
      // localStorage (not sessionStorage) so the session survives closing the tab.
      localStorage.setItem(TOKEN_KEY, token);
      setUser(u);
      setStatus('signedIn');
      clearTimer();
      // Shortly before expiry, silently fetch a new token instead of signing out.
      // If that fails, the next authed request 401s and we sign out then.
      expiryTimer.current = window.setTimeout(silentRefresh, Math.max(1, ttl - 45) * 1000);
    },
    [signOut, silentRefresh],
  );

  // --- Dev no-auth mode: synthesize a session immediately (admin, so the
  //     Team panel is visible while iterating on the UI). ---
  useEffect(() => {
    if (!DEV_DATA) return;
    setUser({ email: 'dev@cosyworld.local', name: 'Dev User', initials: 'DV' });
    setIsAdmin(true);
    setStatus('signedIn');
  }, []);

  // --- Resolve admin status from the server once signed in (prod only). ---
  useEffect(() => {
    if (DEV_DATA || status !== 'signedIn') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authedFetch('/api/me');
        if (!res.ok) return;
        const me = (await res.json()) as { isAdmin?: boolean };
        if (!cancelled) setIsAdmin(Boolean(me.isAdmin));
      } catch {
        /* leave isAdmin false */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status]);

  // --- Restore a non-expired token (persisted across tab closes). ---
  useEffect(() => {
    if (DEV_DATA) return;
    const saved = localStorage.getItem(TOKEN_KEY);
    if (saved && secondsUntilExpiry(saved) > 0) adoptToken(saved);
    // else: stay 'loading' — the GIS-ready effect attempts a silent sign-in,
    // then falls back to the login screen if that doesn't resolve.
  }, [adoptToken]);

  // --- Initialize GIS once the script + client ID are available. ---
  useEffect(() => {
    if (DEV_DATA || !CLIENT_ID) return;
    let cancelled = false;
    const tryInit = () => {
      if (cancelled) return;
      if (!window.google?.accounts?.id) {
        window.setTimeout(tryInit, 120);
        return;
      }
      if (!initialized.current) {
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID,
          callback: (resp) => adoptToken(resp.credential),
          auto_select: true, // silently re-issue a token for returning users
          cancel_on_tap_outside: true,
        });
        initialized.current = true;
        // No valid stored token → try a silent sign-in; if it doesn't resolve
        // shortly, show the login screen.
        if (tokenRef.current == null) {
          silentRefresh();
          window.setTimeout(() => {
            if (tokenRef.current == null) setStatus((s) => (s === 'signedIn' ? s : 'signedOut'));
          }, 2500);
        }
      }
      setGoogleReady(true);
    };
    tryInit();
    return () => {
      cancelled = true;
    };
  }, [adoptToken, silentRefresh]);

  const renderButton = useCallback((el: HTMLElement) => {
    if (!window.google?.accounts?.id) return;
    el.innerHTML = '';
    window.google.accounts.id.renderButton(el, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: 312,
      locale: 'de',
    });
  }, []);

  const authedFetch = useCallback(
    async (input: RequestInfo, init: RequestInit = {}) => {
      const headers = new Headers(init.headers);
      if (tokenRef.current) headers.set('Authorization', `Bearer ${tokenRef.current}`);
      const res = await fetch(input, { ...init, headers });
      if (res.status === 401) signOut();
      return res;
    },
    [signOut],
  );

  const value = useMemo<AuthValue>(
    () => ({ status, user, googleReady, configError, devMode: DEV_DATA, isAdmin, renderButton, signOut, authedFetch }),
    [status, user, googleReady, configError, isAdmin, renderButton, signOut, authedFetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
